/**
 * Global multi-highlight map (W1-06; F§11.8, R§4.2, S§3.3), maintained incrementally per line.
 *
 * Phira's rule: for each line, group its notes by exactly equal f64 time and push each group's time once, twice
 * when the group has >= 2 notes; a time is "multi" when it was pushed >= 2 times overall. Equivalently every line
 * contributes `min(count on that line, 2)` to a global per-time counter and a note is highlighted when the counter
 * of its time is >= 2. Fake notes and holds (by their head time) count. The comparison is exact f64 equality
 * (Map keys: SameValueZero, so +0 and -0 are equal like f64 `==`); NaN times never match anything.
 *
 * Updating a line subtracts its previous contribution and adds the new one. The epoch is bumped only when the
 * multi status of some time flipped, so per-line flag caches (LineNotesRt.mh) stay valid across unrelated edits.
 */

import type { LineId } from '../../model/types';
import type { LineNotesRt } from './runtime';

export class MultiHintMap {
  /** time -> sum over lines of min(count on line, 2). */
  private readonly counts = new Map<number, number>();
  /** Contribution currently applied per line. */
  private readonly applied = new Map<LineId, ReadonlyMap<number, number>>();
  /** Incremented whenever the multi status of at least one time changed. */
  epoch = 0;

  /** Contribution of one runtime: time -> min(count, 2). */
  static contributionOf(rt: LineNotesRt): Map<number, number> {
    const c = new Map<number, number>();
    for (let i = 0; i < rt.n; i++) {
      const t = rt.time[i] ?? Number.NaN;
      if (Number.isNaN(t)) continue;
      const prev = c.get(t) ?? 0;
      if (prev < 2) c.set(t, prev + 1);
    }
    return c;
  }

  /** Replaces the contribution of `line` (null = the line is gone). Returns true when some status flipped. */
  setLine(line: LineId, rt: LineNotesRt | null): boolean {
    const old = this.applied.get(line);
    const next = rt === null ? null : MultiHintMap.contributionOf(rt);
    let flipped = false;
    if (old !== undefined) {
      for (const [t, k] of old) flipped = this.add(t, -k) || flipped;
      this.applied.delete(line);
    }
    if (next !== null) {
      for (const [t, k] of next) flipped = this.add(t, k) || flipped;
      this.applied.set(line, next);
    }
    if (flipped) this.epoch++;
    return flipped;
  }

  /** Whether any note anywhere shares the bit-identical time `t` with another note. */
  isMulti(t: number): boolean {
    return (this.counts.get(t) ?? 0) >= 2;
  }

  /** Lines with an applied contribution. */
  lines(): IterableIterator<LineId> {
    return this.applied.keys();
  }

  private add(t: number, k: number): boolean {
    const before = this.counts.get(t) ?? 0;
    const after = before + k;
    if (after <= 0) this.counts.delete(t);
    else this.counts.set(t, after);
    return before >= 2 !== after >= 2;
  }
}

/** Refreshes `rt.mh` from the map when its cache is older than the map's epoch. */
export function refreshMultiFlags(rt: LineNotesRt, map: MultiHintMap): void {
  if (rt.mhEpoch === map.epoch) return;
  for (let i = 0; i < rt.n; i++) rt.mh[i] = map.isMulti(rt.time[i] ?? Number.NaN) ? 1 : 0;
  rt.mhEpoch = map.epoch;
}
